import type { PaymentMethods } from "@commercekit/api";
import { useErrorAlert } from "@commercekit/react";
import type { FC } from "react";
import React, { createContext, useContext, useEffect, useRef } from "react";
import { useFetcherResult } from "~/lib/hooks/useActionResult";
import { useCheckout } from "~/lib/hooks/useCheckout";

export enum CheckoutPaymentError {
  LOAD_PAYMENT_METHODS_FAILED,
  EMPTY_CART_ITEMS,
  INVALID_PHONENUMBER,
  INVALID_COC_NUMBER,
}

export interface PaymentMethodsWithIcon extends PaymentMethods {
  image: string | null;
  icon?: string | null;
  desc: string;
}

const CheckoutPaymentCtx = createContext<{
  paymentMethods: PaymentMethodsWithIcon[];
  returnUrl: string;
  error: CheckoutPaymentError | null;
  paymentMethod: React.MutableRefObject<Pick<PaymentMethodsWithIcon, "id" | "name"> | undefined>;
  sortOrder?: string[];
  setPaymentMethod: (v: Pick<PaymentMethodsWithIcon, "id" | "name">) => void;
}>({
  paymentMethods: [],
  returnUrl: "",
  error: null,
  paymentMethod: { current: undefined },
  sortOrder: [],
  setPaymentMethod: () => {},
});

export const CheckoutPaymentCtxProvider: FC<
  React.PropsWithChildren<{
    paymentMethods: PaymentMethodsWithIcon[];
    sortOrder?: string[];
    returnUrl: string;
  }>
> = ({ children, paymentMethods, sortOrder, returnUrl }) => {
  const { checkout } = useCheckout();

  let error =
    paymentMethods && paymentMethods?.length === 0
      ? CheckoutPaymentError.LOAD_PAYMENT_METHODS_FAILED
      : !checkout?.lineItems?.length
      ? CheckoutPaymentError.EMPTY_CART_ITEMS
      : null;
  const paymentMethod = useRef<Pick<PaymentMethodsWithIcon, "id" | "name">>();

  const sortedPaymentMethods = React.useMemo(() => {
    const orderMap = new Map<string, number>((sortOrder ?? []).map((item, index) => [item, index]));

    return [...paymentMethods].sort((a, b) => {
      const aIndex = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
      const bIndex = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
      return aIndex - bIndex;
    });
  }, [sortOrder, paymentMethods]);
  return (
    <CheckoutPaymentCtx.Provider
      value={{
        paymentMethods: sortedPaymentMethods,
        returnUrl,
        error,
        paymentMethod,
        setPaymentMethod: (v: Pick<PaymentMethodsWithIcon, "id" | "name">) => (paymentMethod.current = v),
      }}
    >
      {children}
    </CheckoutPaymentCtx.Provider>
  );
};

export const useCheckoutPayment = () => useContext(CheckoutPaymentCtx);

export const usePaymentFetcher = () => {
  const { show } = useErrorAlert();
  const { fetcher, isLoading, error } = useFetcherResult();
  useEffect(() => {
    error && show(error.join("; "), "warning");
  }, [error, show]);
  return { loading: isLoading, fetcher, error };
};
